/**
 * Weight of the six quality criteria in the project's score (#874, spec
 * docs/plans/2026-10-05-quality-weights.md §8).
 *
 * Its own resource, dirty state and Save — not the tab's shared Save, which
 * sends one PATCH of the project row. Saving always goes through the
 * confirmation dialog: new weights rescore the project's whole history.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import type { QualityWeights } from '@aica/api-types/schemas';
import { canonicalizeQualityWeights, QUALITY_SUB_METRIC_KEYS } from '@aica/core';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useReportUnsavedSection } from '@/components/settings/UnsavedChangesGuard';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import {
  isQualityWeightsConflict,
  useQualityWeights,
  useQualityWeightsPreview,
  useSaveQualityWeights,
} from '../hooks/useQualityWeights';
import { QualityWeightsConfirmDialog } from './QualityWeightsConfirmDialog';

/** The weight every criterion shows when the project counts them equally. */
export const EQUAL_QUALITY_WEIGHT = 5;
const MIN_WEIGHT = 0;
const MAX_WEIGHT = 10;

const equalWeights = (): QualityWeights =>
  Object.fromEntries(
    QUALITY_SUB_METRIC_KEYS.map((key) => [key, EQUAL_QUALITY_WEIGHT])
  ) as QualityWeights;

const isAllZero = (weights: QualityWeights): boolean =>
  QUALITY_SUB_METRIC_KEYS.every((key) => weights[key] === 0);

/** One form of "the same weights": `null` and any equal sheet compare alike. */
const sameWeights = (a: unknown, b: unknown): boolean =>
  JSON.stringify(canonicalizeQualityWeights(a)) ===
  JSON.stringify(canonicalizeQualityWeights(b));

export function QualityWeightsSection({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation(['settings', 'records']);
  const { toast } = useToast();
  const query = useQualityWeights(projectId);
  const preview = useQualityWeightsPreview(projectId);
  const save = useSaveQualityWeights(projectId);
  const [draft, setDraft] = useState<QualityWeights | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The saved weights the preview compared the draft against, taken when the
  // preview starts: the PUT expects exactly these, so a refetch between the
  // preview and the confirm cannot slip someone else's save past the dialog.
  const [baseline, setBaseline] = useState<QualityWeights | null>(null);
  // The section saves itself, outside the tab's shared form: leaving the
  // settings with an unsaved draft must be asked about like any other edit.
  // An all-zero draft is always an edit: it cannot be saved, and it reads as
  // `null` like the equal sheet, so `sameWeights` alone would call it clean.
  const dirty =
    draft !== undefined &&
    !!query.data &&
    (isAllZero(draft) || !sameWeights(draft, query.data.weights));
  useReportUnsavedSection('project-settings:quality-weights', dirty);

  if (query.isLoading) return null;
  // Only a load that never produced data is an error here: a failed background
  // refetch keeps `data` (TanStack v5), and the editor — with its draft — stays.
  if (!query.data) {
    return (
      <Alert variant="danger" data-testid="quality-weights-load-error">
        <AlertDescription>{t('qualityTab.weights.loadError')}</AlertDescription>
      </Alert>
    );
  }

  // All six as the score reads them (a missing key takes its default), or the
  // equal sheet.
  const saved = canonicalizeQualityWeights(query.data.weights) ?? equalWeights();
  const weights = draft ?? saved;
  const total = QUALITY_SUB_METRIC_KEYS.reduce((sum, key) => sum + weights[key], 0);
  const allZero = isAllZero(weights);
  const equal = canonicalizeQualityWeights(weights) === null && !allZero;
  const percent = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  const setWeight = (key: keyof QualityWeights, value: number) =>
    setDraft({ ...weights, [key]: Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, value)) });

  const openConfirm = () => {
    setBaseline(canonicalizeQualityWeights(query.data.weights));
    setConfirmOpen(true);
    preview.mutate(weights);
  };

  const confirm = () => {
    save.mutate(
      { weights, expected: baseline },
      {
        onSuccess: () => {
          toast.success(t('qualityTab.weights.saved'));
          setDraft(undefined);
          setConfirmOpen(false);
        },
        onError: (error) => {
          if (isQualityWeightsConflict(error)) {
            // The draft stays: the owner reviews it against the weights saved
            // meanwhile (reloaded by the hook) and saves again with a fresh preview.
            toast.error(t('qualityTab.weights.conflict'));
            setConfirmOpen(false);
            return;
          }
          logError('quality-weights.save_failed', {
            component: 'QualityWeightsSection',
            projectId,
            error: error instanceof Error ? error.message : String(error),
          });
          toast.error(t('qualityTab.weights.saveError'));
        },
      }
    );
  };

  return (
    <div
      className="settings-section border border-border rounded-card p-4"
      data-testid="quality-weights-section"
    >
      <h3 className="text-sm font-medium text-fg mb-1">
        {t('qualityTab.weights.title')}
      </h3>
      <p className="text-xs text-fg-muted mb-4">{t('qualityTab.weights.description')}</p>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-fg-muted">
            <th className="pb-2 font-medium">{t('qualityTab.weights.criterion')}</th>
            <th className="pb-2 font-medium">{t('qualityTab.weights.importance')}</th>
            <th className="pb-2 font-medium text-right">
              {t('qualityTab.weights.share')}
            </th>
          </tr>
        </thead>
        <tbody>
          {QUALITY_SUB_METRIC_KEYS.map((key) => {
            const criterion = t(`records:records.componentScores.labels.${key}`);
            const weight = weights[key];
            return (
              <tr
                key={key}
                className="border-t border-border"
                data-testid={`weight-row-${key}`}
              >
                <td className="py-2 text-fg">{criterion}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label={t('qualityTab.weights.decrease', { criterion })}
                      disabled={weight <= MIN_WEIGHT}
                      onClick={() => setWeight(key, weight - 1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span
                      className="w-6 text-center font-mono text-sm text-fg tabular-nums"
                      data-testid={`weight-value-${key}`}
                    >
                      {weight}
                    </span>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label={t('qualityTab.weights.increase', { criterion })}
                      disabled={weight >= MAX_WEIGHT}
                      onClick={() => setWeight(key, weight + 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
                <td
                  className="py-2 text-right font-mono text-fg-secondary tabular-nums"
                  data-testid={`weight-share-${key}`}
                >
                  {allZero
                    ? '—'
                    : weight === 0
                      ? t('qualityTab.weights.notCounted')
                      : t('qualityTab.weights.shareValue', {
                          value: percent.format((weight / total) * 100),
                        })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {allZero ? (
        <p className="mt-3 text-xs text-warning" data-testid="quality-weights-all-zero">
          {t('qualityTab.weights.allZero')}
        </p>
      ) : (
        equal && (
          <p className="mt-3 text-xs text-fg-muted" data-testid="quality-weights-equal">
            {t('qualityTab.weights.equalHint')}
          </p>
        )
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={equal}
          onClick={() => setDraft(equalWeights())}
        >
          {t('qualityTab.weights.reset')}
        </Button>
        <Button
          size="sm"
          disabled={!dirty || allZero}
          onClick={openConfirm}
          data-testid="quality-weights-save"
        >
          {t('qualityTab.weights.save')}
        </Button>
      </div>

      <QualityWeightsConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!save.isPending) setConfirmOpen(open);
        }}
        preview={preview}
        onRetryPreview={() => preview.mutate(weights)}
        isSaving={save.isPending}
        onConfirm={confirm}
      />
    </div>
  );
}
